import React from 'react';
import { SecondaryButton } from 'ui/button';
import { ErrorPage, ErrorPageIcon } from 'core/errorPage';
import { WarningBanner } from 'ui/banner';
import { Divider } from 'ui/divider';
import { PageHeaderInlineEdit } from 'ui/inlineEdit';
import { PageHeader } from 'ui/pageHeader';
import CollectionsHandlebarsModal from 'modules/catalog/collectionsHandlebarsModal';
import { COLLECTIONS } from 'modules/catalog/breadcrumbs';
import {
  CATALOG_COLLECTION_NAME,
  CATALOG_COLLECTIONS_NAME,
  CATALOG_COLLECTIONS_APP_URL,
  COLLECTION_NAME,
  NEW_COLLECTION_NAME,
} from 'modules/catalog/catalogConstants';
import {
  CATALOG_EMPTY_SEARCH_GROUP,
  CATALOG_EMPTY_SEARCH_QUERY,
  CATALOG_EMPTY_CRITERIA_PROPERTY,
} from 'modules/catalog/helpers';
import noop from 'utils/noop';
import { AND } from 'utils/combinatorTypes';
import { isAlphaNumericDashesSpaces, noLeadingTrailingSpaces } from 'utils/validation';
import { CollectionQueryBuilder } from '../../legacyQueryBuilder';
import { CatalogsDropdown } from './catalogsDropdown';
import DescriptionInput from './descriptionInput';
import { PreviewResultsBar } from './previewResultsBar/PreviewResultsBar';
import PreviewResultsTable from './previewResultsTable';
import {
  CollectionBuilderContainer,
  PageHeaderInlineEditContainer,
} from './CollectionsBuilder.styled';
import {
  checkIfExistingCollection,
  handleSaveQueryValidation,
  updateFields,
  handleChangeCatalog,
  handleOnComponentUpdateCatalogChange,
} from './CollectionBuilder.utils';

// type Props = {
//   /** Whether user has RBAC permission to manage catalogs */
//   canManageCatalogs: Boolean,
//   /** Whether user has permission to manage user profiles and data */
//   canManageUserProfiles: Boolean,
//   /** Whether user has permission to view and export user data */
//   canViewAndExportUserData: Boolean,
//   /** Value of the selected catalog. */
//   catalog: string,
//   /** List of catalogs. */
//   catalogs: [],
//   /** The fields of the selected catalog. */
//   catalogFields: {},
//   /** Object of all the field types in the catalog. */
//   catalogFieldTypes: {},
//   /**
//    * Callback function when catalog is changed.
//    *
//    * ```
//    * changeCatalog(name)
//    * ```
//    * @param { String } catalog: The name of the selected catalog.
//    * @param { Boolean } fetchFieldTypes: If fields types should be fetched.
//    * @returns { void }
//    */
//   changeCatalog: (catalog: string, fetchFieldTypes: boolean) => {},
//   /**
//    * Callback function when description is changed.
//    *
//    * ```
//    * changeCollectionDescription(name)
//    * ```
//    * @param { String } description: The updated description.
//    * @returns { void }
//    */
//   changeCollectionDescription: (name: string) => {},
//   /**
//    * Callback function when name is changed.
//    *
//    * ```
//    * changeCollectionName(name)
//    * ```
//    * @param { String } name: The updated name.
//    * @returns { void }
//    */
//   changeCollectionName: (name: string) => {},
//   /**
//    * Callback function when fields are changed.
//    *
//    * ```
//    * changeCurrentFields(currentFields)
//    * ```
//    * @param { Array } currentFields: The current fields.
//    * @returns { void }
//    */
//   changeCurrentFields: (currentFields: []) => {},
//   /**
//    * Callback function when query is created.
//    *
//    * ```
//    * createCollection()
//    * ```
//    * @returns { void }
//    */
//   createCollection: () => {},
//   /** The current fields of the collection builder. */
//   currentFields: [],
//   /** The description of the query. */
//   description: string,
//   /** If data has been previewed. */
//   didPreviewData: boolean,
//   /** The value of an empty search group. */
//   emptySearchGroup: {},
//   /** The value of an empty search query. */
//   emptySearchQuery: {},
//   /** The value of an empty criteria property . */
//   emptyCriteriaProperty: {},
//   /** Error object of request. */
//   error: {},
//   /** If collection is contains a dynamic field. */
//   hasDynamicFields: boolean,
//   /** The id of the collection. */
//   id: number,
//   /** If currently fetching catalogs. */
//   isFetchingCatalogs: boolean,
//   /** If currently fetching query. */
//   isFetchingCollection: boolean,
//   /** If currently loading preview. */
//   isLoadingPreview: boolean,
//   /** If the ProductRecommendations feature flag is on. */
//   isProductRecommendationsOn: Boolean,
//   /** If the DefaultCombinatorsToAll feature flag is on. */
//   isAllCombinatorSearchQueryOn: Boolean,
//   /** If currently saving the catalog collection. */
//   isSaving: boolean,
//   /** Number of milliseconds to dleay loading. */
//   loadingDelay: number,
//   /** The name of the query. */
//   name: string,
//   /**
//    * Function to open handlebars modal.
//    * ```
//    * openHandlebarsModal(selectedCollectionName)
//    * ```
//    * @param { String } selectedCollectionName: The selected collection name.
//    * @returns { void }
//    */
//   openHandlebarsModal: (selectedCollectionName: string) => void,
//   /** The dynamic orderBy location. */
//   orderByDynamicLocation: ?string,
//   /** Data from previewing collection. */
//   previewData: [],
//   /**
//    * Callback function to preview collection.
//    *
//    * ```
//    * previewCollection(previewIdentifier)
//    * ```
//    * @param { String } previewIdentifier: The user identifier to preview query with
//    * @returns { void }
//    */
//   previewCollection: (previewIdentifier: string) => {},
//   /** If the query has changed. */
//   collectionHasChanged: boolean,
//   /** If the query is not found. */
//   collectionNotFound: boolean,
//   /** Store object to get state. (Used for connected CreateCollectionModal component) */
//   store: {},
//   /**
//    * Callback function to update fields in the query builder state.
//    *
//    * ```
//    * updateFieldsForProperty(property, fields)
//    * ```
//    * @param { String } property: The property name.
//    * @param { Array } field: The fields of the property.
//    * @returns { void }
//    */
//   updateFieldsForProperty: (property: string, fields: []) => {},
//   /**
//    * Callback function when collection is updated.
//    *
//    * ```
//    * createCollection(id)
//    * ```
//    * @param { Number } id: The id of the collection to update.
//    * @returns { void }
//    */
//   updateCollection: () => {},
//   /** If the catalog uses product recommendations to order the query results. */
//   usesSmartRecos: Boolean,
//   /**
//    * Callback function to validate query.
//    *
//    * ```
//    * validateQuery(callback, props)
//    * ```
//    * @param { Function } callback: Callback function if query is valid.
//    * @param { Array } props: Props passed to callback function.
//    * @returns { Any }
//    */
//   validateQuery: (callback: () => *, props) => {},
//   /** UUID project type */
//   uuidProjectType: UuidProjectType,
//   /** Whether or not the current project uses UUIDs for identifying users */
//   isUuidBased: boolean,
// };

export default class CollectionBuilder extends React.Component {
  static defaultProps = {
    catalogFields: {},
    changeCatalog: noop,
    changeName: noop,
    changeCurrentFields: noop,
    createCollection: noop,
    currentFields: [],
    emptyCriteriaProperty: CATALOG_EMPTY_CRITERIA_PROPERTY,
    emptySearchGroup: CATALOG_EMPTY_SEARCH_GROUP,
    emptySearchQuery: CATALOG_EMPTY_SEARCH_QUERY,
    error: {},
    hasDynamicFields: false,
    isProductRecommendationsOn: false,
    isAllCombinatorSearchQueryOn: false,
    loadingDelay: 400,
    name: NEW_COLLECTION_NAME,
    openHandlebarsModal: noop,
    previewCollection: noop,
    collectionHasChanged: false,
    searchGroups: [],
    updateFieldsForProperty: noop,
    updateCollection: noop,
    usesSmartRecos: false,
    validateQuery: noop,
  };

  state = {
    isLoadingContent: true,
    previewIdentifier: '',
  };

  /**
   * componentDidUpdate
   * Gets called after catalog changes and fields are updated. This function will put the fields in
   * the query builder state to populate the field dropdown,
   * @param { Object } prevProps: The previous prop values.
   * @returns { void }
   */
  componentDidUpdate = (prevProps) => {
    const { catalog: prevCatalog } = prevProps;
    const {
      catalog,
      catalogFields,
      changeCurrentFields,
      currentFields,
      loadingDelay,
      updateFieldsForProperty,
    } = this.props;

    handleOnComponentUpdateCatalogChange(prevCatalog, catalog, this.handleChangeCatalog);
    updateFields({ catalogFields, changeCurrentFields, currentFields, updateFieldsForProperty });
    setTimeout(this.finishLoadingContent, loadingDelay);
  };

  handleRef = (ref) => {
    this.ref = ref;
  };

  finishLoadingContent = () => {
    if (this.state.isLoadingContent)
      this.setState((prev) => ({ ...prev, isLoadingContent: false }));
  };

  /**
   * canSave
   * Determines if the query can be saved if there has been changes.
   * @returns { Boolean } If the query can be saved.
   */
  canSave = () => {
    const { catalog, name, collectionHasChanged } = this.props;
    return catalog && name && collectionHasChanged;
  };

  /**
   * handleChangeCatalog
   * Callback function when catalog changes. Prevents c=multiple calls is changing to same catalog.
   * @param { String } catalogName: The name of the catalog to change to.
   * @param { Boolean } initialize: Passed back by componentDidMount to force change catalog.
   * @returns { void }
   */
  handleChangeCatalog = (catalogName, initialize) => {
    const { catalog, changeCatalog } = this.props;
    handleChangeCatalog({ catalog, catalogName, initialize, changeCatalog });
  };

  /**
   * handleClickOpenHandlerbarsModal
   * Callback function when clicking on button to open handlebars modal.
   * @returns { void }
   */
  handleClickOpenHandlerbarsModal = () => {
    const { catalog, id, name, openHandlebarsModal } = this.props;
    openHandlebarsModal(name, { catalogName: catalog, id, name });
  };

  /**
   * handleSaveQuery
   * Callback function when query is save. Determines if query is created or updated.
   * @returns { void }
   */
  handleSaveQuery = () => {
    const { createCollection, id, updateCollection, validateQuery } = this.props;
    handleSaveQueryValidation({ createCollection, id, updateCollection, validateQuery });
  };

  handlePreviewQuery = (previewIdentifier /*: string */, isEmail /*: boolean */) => {
    const { id, previewCollection, validateQuery } = this.props;
    validateQuery(
      previewCollection,
      previewIdentifier,
      isEmail,
      this.isExistingCollection(id),
      this.canSave(),
    );
    this.ref.scrollIntoView({ behavior: 'smooth' });
  };

  isExistingCollection = checkIfExistingCollection;

  /**
   * getEmptySearchGroup
   * Function that updates empty search group with "And" combinator if "isAllCombinatorSearchQueryOn" is true
   */
  getEmptySearchGroup = () => {
    const { isAllCombinatorSearchQueryOn, emptySearchGroup } = this.props;
    return isAllCombinatorSearchQueryOn
      ? { ...emptySearchGroup, combinator: AND }
      : emptySearchGroup;
  };

  setPreviewIdentifier = (value /*: string */) => {
    this.setState({ previewIdentifier: value });
  };

  render() {
    const {
      canManageCatalogs,
      canManageUserProfiles,
      canViewAndExportUserData,
      catalog,
      catalogs,
      catalogFieldTypes,
      changeCollectionDescription,
      description,
      didPreviewData,
      emptyCriteriaProperty,
      emptySearchQuery,
      error,
      hasDynamicFields,
      id,
      isFetchingCatalogs,
      isFetchingCollection,
      isLoadingPreview,
      isProductRecommendationsOn,
      isSaving,
      name,
      orderByDynamicLocation,
      previewData,
      collectionNotFound,
      store,
      usesSmartRecos,
      uuidProjectType,
      isUuidBased,
      changeCollectionName,
    } = this.props;
    const { isLoadingContent, previewIdentifier } = this.state;
    const canManageUserProfilesAndData = canManageUserProfiles && canViewAndExportUserData;

    if (collectionNotFound) {
      return (
        <ErrorPage
          {...error}
          backText={`Back to ${CATALOG_COLLECTIONS_NAME}`}
          backTo={CATALOG_COLLECTIONS_APP_URL}
          header={`${CATALOG_COLLECTION_NAME} Does Not Exist`}
          icon={ErrorPageIcon.CatalogCollections}
        />
      );
    }

    const invalidNameMessages = [
      ...isAlphaNumericDashesSpaces(name),
      ...noLeadingTrailingSpaces(name),
    ];
    const isExistingCollection = this.isExistingCollection(id);
    const isNameInvalid = invalidNameMessages.length > 0;
    const canSave = this.canSave();
    const loadingMessage = `Loading...`;
    const updatedEmptySearchGroup = this.getEmptySearchGroup();

    return (
      <CollectionBuilderContainer>
        <PageHeader
          breadcrumb={COLLECTIONS}
          pageActions={
            <SecondaryButton
              isDisabled={!isExistingCollection}
              onClick={this.handleClickOpenHandlerbarsModal}
            >
              Use in Template
            </SecondaryButton>
          }
        >
          <PageHeaderInlineEditContainer>
            <PageHeaderInlineEdit
              value={name}
              onChange={canManageCatalogs ? changeCollectionName : undefined}
              placeholder={isFetchingCollection ? loadingMessage : `${COLLECTION_NAME} Name`}
            />
          </PageHeaderInlineEditContainer>
        </PageHeader>
        {isNameInvalid && (
          <WarningBanner>
            <div>Your {CATALOG_COLLECTION_NAME} Name</div>
            <div>{invalidNameMessages}</div>
          </WarningBanner>
        )}
        <div className={`catalogsDropdownContainer ${isLoadingContent ? 'loadingHeader' : ''}`}>
          <CatalogsDropdown
            catalogs={catalogs}
            disabled={isExistingCollection || !!catalog}
            isLoading={isFetchingCatalogs}
            onChange={this.handleChangeCatalog}
            value={catalog}
          />
          <DescriptionInput
            isLoading={isFetchingCollection}
            onChange={canManageCatalogs ? changeCollectionDescription : null}
            value={description}
          />
        </div>
        <Divider />
        <CollectionQueryBuilder
          limitAndOrderByRowContent={
            <PreviewResultsBar
              canManageCatalogs={canManageCatalogs}
              canManageUserProfilesAndData={canManageUserProfilesAndData}
              canSave={canSave}
              disabled={isNameInvalid || isSaving}
              hasDynamicFields={hasDynamicFields}
              isLoading={isLoadingPreview}
              onClickPreview={this.handlePreviewQuery}
              onClickSave={this.handleSaveQuery}
              orderByDynamicLocation={orderByDynamicLocation}
              usesSmartRecos={isProductRecommendationsOn ? usesSmartRecos : undefined}
              previewIdentifier={previewIdentifier}
              setPreviewIdentifier={this.setPreviewIdentifier}
              uuidProjectType={uuidProjectType}
              isUuidBased={isUuidBased}
            />
          }
          resetPreviewIdentifier={() => this.setPreviewIdentifier('')}
          {...{
            emptyCriteriaProperty,
            emptySearchGroup: updatedEmptySearchGroup,
            emptySearchQuery,
          }}
        />
        <div ref={this.handleRef} />
        <PreviewResultsTable
          fieldTypes={catalogFieldTypes}
          didPreviewData={didPreviewData}
          isLoading={isLoadingPreview}
          rows={previewData}
        />
        <CollectionsHandlebarsModal store={store} />
      </CollectionBuilderContainer>
    );
  }
}
